import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, useTheme, MenuItem, Select } from "@mui/material";
import { CalendarToday, Edit, Warning } from "@mui/icons-material";
import { useToday } from "../../../../../../utils/dateUtils";
import DatePicker from "../../../../../../components/DatePicker/DatePicker";

export const ExceptionDetailsForm = ({
  reason,
  setReason,
  validUntil,
  setValidUntil,
  isValidUntilPast,
}) => {
  const theme = useTheme();
  const today = useToday();
  const [selectedDuration, setSelectedDuration] = useState("");
  const [showCustomDate, setShowCustomDate] = useState(false);

  const getDateForDuration = (months, years = 0) => {
    const date = new Date();
    if (years > 0) {
      date.setFullYear(date.getFullYear() + years);
    } else {
      date.setMonth(date.getMonth() + months);
    }
    return date.toISOString().split('T')[0];
  };

  const predefinedOptions = [
    { label: "1 Month", value: getDateForDuration(1) },
    { label: "2 Months", value: getDateForDuration(2) },
    { label: "3 Months", value: getDateForDuration(3) },
    { label: "6 Months", value: getDateForDuration(6) },
    { label: "1 Year", value: getDateForDuration(0, 1) },
  ];

  useEffect(() => {
    const matchesPredefined = predefinedOptions.find(opt => opt.value === validUntil);
    if (matchesPredefined) {
      setSelectedDuration(validUntil);
      setShowCustomDate(false);
    } else if (validUntil) {
      setSelectedDuration("custom");
      setShowCustomDate(true);
    } else {
      setSelectedDuration("");
      setShowCustomDate(false);
    }
  }, [validUntil]);

  const handleDurationChange = (value) => {
    if (value === "custom") {
      setSelectedDuration("custom");
      setShowCustomDate(true);
      setValidUntil("");
    } else {
      setSelectedDuration(value);
      setShowCustomDate(false);
      setValidUntil(value);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        sx={{
          mb: 1,
          fontWeight: 600,
          color: theme.palette?.text?.primary,
          fontFamily: theme.typography?.fontFamily,
        }}
      >
        Justification
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette?.text?.secondary,
          mb: 3,
          fontFamily: theme.typography?.fontFamily,
        }}
      >
        Provide justification and set expiration date
      </Typography>

      <Box display="flex" flexDirection="column" gap={3}>
        <Box>
          <TextField
            fullWidth
            value={reason}
            onChange={(e) => setReason(e?.target?.value)}
            required
            multiline
            rows={4}
            placeholder="Provide a detailed justification for why this exception is necessary?..."
            variant="outlined"
            sx={{
              fontFamily: theme.typography?.fontFamily,
              "& .MuiOutlinedInput-root": {
                borderRadius:
                  theme.components?.MuiButton?.styleOverrides?.root
                    ?.borderRadius || 1,
                backgroundColor: "#f5f5f5",
                "&:hover": { backgroundColor: "white" },
                "&.Mui-focused": {
                  backgroundColor: "white",
                  "& fieldset": {
                    borderColor: theme.palette?.primary?.main,
                    borderWidth: "2px",
                  },
                },
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: theme.palette?.text?.primary,
              fontFamily: theme.typography?.fontFamily,
            }}
          >
            Disable For
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={selectedDuration}
            onChange={(e) => handleDurationChange(e.target.value)}
            renderValue={(value) =>
              value ? predefinedOptions.find((opt) => opt.value === value)?.label || "Custom Date" : "Select duration"
            }
            MenuProps={{
              disablePortal: false,
              container: document.body,
              style: {
                zIndex: 14001,
              },
            }}
            sx={{
              fontFamily: theme.typography?.fontFamily,
              "& .MuiOutlinedInput-root": {
                borderRadius:
                  theme.components?.MuiButton?.styleOverrides?.root?.borderRadius || 1,
                backgroundColor: "#f5f5f5",
                "&:hover": { backgroundColor: "white" },
                "&.Mui-focused": {
                  backgroundColor: "white",
                  "& fieldset": {
                    borderColor: theme.palette?.primary?.main,
                    borderWidth: "2px",
                  },
                },
              },
              mb: showCustomDate ? 2 : 0,
            }}
          >
            <MenuItem disabled value="">
              <Typography color="text.secondary">Select duration</Typography>
            </MenuItem>
            {predefinedOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
            <MenuItem value="custom">Custom Date</MenuItem>
          </Select>

          {showCustomDate && (
            <DatePicker
              fullWidth
              label="Custom Date"
              value={validUntil || ""}
              onChange={(date) => setValidUntil(date)}
              error={isValidUntilPast}
              minDate={today}
              sx={{
                fontFamily: theme.typography?.fontFamily,
                "& .MuiOutlinedInput-root": {
                  borderRadius:
                    theme.components?.MuiButton?.styleOverrides?.root
                      ?.borderRadius || 1,
                  backgroundColor: "#f5f5f5",
                  "&:hover": { backgroundColor: "white" },
                  "&.Mui-focused": {
                    backgroundColor: "white",
                    "& fieldset": {
                      borderColor: theme.palette?.primary?.main,
                      borderWidth: "2px",
                    },
                  },
                  "&.Mui-error": {
                    "& fieldset": { borderColor: theme.palette?.error?.main },
                  },
                },
              }}
            />
          )}
          {isValidUntilPast && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <Warning
                sx={{ fontSize: 16, color: theme.palette?.error?.main }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette?.error?.main,
                  fontFamily: theme.typography?.fontFamily,
                }}
              >
                Expiry date cannot be in the past
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
